import {
  Box,
  Button,
  Grid,
  MenuItem,
  Modal,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import * as yup from "yup";
import { useEffect, useState } from "react";
import { useAppStore } from "../../../appStore";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import { useFormik } from "formik";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { api_url, img_url } from "../../../apiUtils";
import { toast } from "react-toastify";
import KeyboardIcon from "@mui/icons-material/Keyboard";
import DownloadIcon from "@mui/icons-material/Download";
import { saveAs } from "file-saver";

function AddPoints() {
  const { setAddPointOpen, depositSent, setDepositSent } = useAppStore();
  const navigate = useNavigate();
  const [files, setFiles] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isOtherPayment, setIsOtherPayment] = useState(false); // State to track if "Other" is selected
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [scannerImage, setScannerImage] = useState(null);
  const [scannerImageName, setScannerImageName] = useState(null);
  const [scannerImageFileName, setScannerImageFileName] = useState(null);
  const [showScanner, setShowScanner] = useState(false);
  const [minimumDepositAmount, setMinimumDepositAmount] = useState(null);
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [scannerPreviewOpen, setScannerPreviewOpen] = useState(false);

  const setsnackbarMessage = useAppStore((state) => state.setSnackbarMessage);
  const setopenSnackbar = useAppStore((state) => state.setOpenSnackbar);

  const validationSchema = yup.object().shape({
    requestedAmount: yup
      .number()
      .required("Points is required")
      .min(
        minimumDepositAmount,
        `Minimum amount should be ${minimumDepositAmount} and above.`
      ),
    paymentMode: yup.string().required("Payment Mode is required"),
    customPaymentMode: yup.string(),
  });

  const handleFileChange = (event) => {
    const inputFiles = event.target.files;
    setFiles(inputFiles);

    // Create image preview URLs
    const filePreviews = Array.from(inputFiles).map((file) =>
      URL.createObjectURL(file)
    );
    setImagePreview(filePreviews);
  };

  // wallet settings

  const getWalletSettings = () => {
    let token = sessionStorage.getItem("accesstoken");
    axios
      .get(`${api_url}wallet/get-common-wallet-settings`, {
        headers: {
          accesstoken: token,
        },
      })
      .then((response) => {
        let data = response.data;
        console.log("data", data);
        if (data.status === "success") {
          setMinimumDepositAmount(
            data.getWalletSettings[0].minimumDepositAmount
          );
          setLoadingSettings(false);
        }
      });
  };

  useEffect(() => {
    getWalletSettings();
  }, []);

  const formik = useFormik({
    initialValues: {
      requestedAmount: "",
      paymentMode: "",
      customPaymentMode: "", // Initial value for custom payment mode
    },
    validationSchema: validationSchema,
    onSubmit: async (values, { resetForm }) => {
      if (isSubmitting) return; // Prevent multiple clicks

      setIsSubmitting(true); // Disable button after first click

      const formData = new FormData();
      formData.append("requestedAmount", values.requestedAmount);
      formData.append(
        "paymentMode",
        isOtherPayment ? values.customPaymentMode : values.paymentMode // Send custom payment mode if "Other" is selected
      );
      if (!files) {
        toast.error("Please upload the payment screenshot.");
        setIsSubmitting(false);
        return;
      }

      // Append files to formData
      if (files) {
        Array.from(files).forEach((file) => {
          formData.append("image", file);
        });
      }

      try {
        const token = sessionStorage.getItem("accesstoken");
        const response = await axios.post(
          `${api_url}wallet/deposit`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
              accesstoken: token,
            },
          }
        );

        const data = response.data;
        if (data.status === "success") {
          setDepositSent(true);
          toast.success("Request processing..");
          navigate("/wallet/manage-points");
          setAddPointOpen(false);
        }
      } catch (error) {
        toast.error("An error occurred while sending the request");
      }

      setIsSubmitting(false); // Re-enable button after request completes
      // Reset the form fields after successful submission
      resetForm();
    },
  });

  const handlePaymentModeChange = (event) => {
    const inputPaymentMode = event.target.value;
    formik.setFieldValue("paymentMode", inputPaymentMode);
    formik.setFieldTouched("paymentMode", true);

    // Check if "Other" is selected and update state
    if (inputPaymentMode === "other") {
      setIsOtherPayment(true);
    } else {
      setIsOtherPayment(false);
    }
  };

  const handleRequestedAmountChange = (event) => {
    const inputRequestedAmount = event.target.value;
    formik.setFieldValue("requestedAmount", inputRequestedAmount);
  };

  const handleCustomPaymentModeChange = (event) => {
    const inputCustomPaymentMode = event.target.value;
    formik.setFieldValue("customPaymentMode", inputCustomPaymentMode);
  };

  // scanner
  // Fetch random scanner image when amount condition is met
  useEffect(() => {
    const fetchRandomScanner = async () => {
      try {
        const response = await axios.get(`${api_url}wallet/get-random-scanner`);
        if (response?.data?.image?.path) {
          setScannerImage(`${img_url}${response.data.image.path}`);
          console.log("setScannerImageName", response.data.image);
          setScannerImageName(response.data.image.originalname);
          setScannerImageFileName(response.data.image.filename);
          setShowScanner(true);
        }
      } catch (error) {
        console.error("Failed to fetch random scanner image", error);
      }
    };

    if (formik.values.requestedAmount >= 300) {
      fetchRandomScanner();
    } else {
      setShowScanner(false);
    }
  }, [formik.values.requestedAmount]);

  return (
    <Box
      sx={{
        maxHeight: "calc(100vh - 120px)",
        overflowY: "auto",
        px: 1,

        "&::-webkit-scrollbar": {
          width: "6px",
        },
        "&::-webkit-scrollbar-track": {
          background: "transparent",
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: "rgba(255, 255, 255, 0.2)",
          borderRadius: "4px",
        },
        "&::-webkit-scrollbar-thumb:hover": {
          backgroundColor: "rgba(255, 255, 255, 0.3)",
        },
        "&::-webkit-scrollbar-button": {
          display: "none",
        },
      }}
    >
      <Box sx={{ textAlign: "center" }} mb={1}>
        <Typography variant="h5">Add Points To Wallet</Typography>
        <Typography variant="caption" sx={{ color: "#bdbdbd" }}>
          Send request to deposit points in wallet
        </Typography>
        <Typography sx={{ color: "#fd0980" }}>
          * Verifying Time : 10:15 AM - 11:30 PM *
        </Typography>
        <Typography
          sx={{
            color: "#4a148c",
            border: "1px dashed #4a148c",
            mt: 1,
            borderRadius: 2,
          }}
        >
          * Please bear with us for{" "}
          <span style={{ fontWeight: "bold" }}>10 Minutes</span> while we{" "}
          <span style={{ fontWeight: "bold", marginRight: 1 }}>Update </span>
          the points. *
        </Typography>
      </Box>
      <Box sx={{ border: "1px dashed #bdbdbd" }} />

      <Box height={10} />
      <Grid container mt={3}>
        <Grid item xs={6} sx={{ display: "flex", alignItems: "center" }}>
          <Typography>Deposit Point :</Typography>
        </Grid>
        <Grid item xs={6}>
          <TextField
            size="small"
            id="requestedAmount"
            name="requestedAmount"
            error={
              formik.touched.requestedAmount && formik.errors.requestedAmount
            }
            helperText={
              formik.touched.requestedAmount && formik.errors.requestedAmount
            }
            value={formik.values.requestedAmount}
            onChange={handleRequestedAmountChange}
            onBlur={formik.handleBlur}
          />
        </Grid>
      </Grid>
      {formik.values.requestedAmount >= minimumDepositAmount &&
        scannerImage && (
          <Grid container mt={3} justifyContent="center">
            <Grid item xs={12} textAlign="center">
              <Typography variant="subtitle1" fontWeight="bold" mb={1}>
                Click On QR And Save In Gallery
              </Typography>
              <Box
                component="img"
                src={scannerImage}
                alt="Scanner QR"
                onClick={() => setScannerPreviewOpen(true)}
                sx={{
                  width: showScanner ? 150 : 200,
                  height: showScanner ? 150 : 200,
                  opacity: showScanner ? 1 : 0,
                  cursor: "pointer",
                  transition: "all 0.5s ease-in-out",
                  transform: showScanner ? "scale(1)" : "scale(0.8)",
                  border: "1px solid #ccc",
                  borderRadius: 2,
                }}
              />
            </Grid>
          </Grid>
        )}

      <Grid container mt={3}>
        <Grid item xs={6} sx={{ display: "flex", alignItems: "center" }}>
          <Typography>Payment Mode :</Typography>
        </Grid>
        <Grid item xs={6}>
          <Select
            fullWidth
            size="small"
            id="paymentMode"
            name="paymentMode"
            error={formik.touched.paymentMode && formik.errors.paymentMode}
            helperText={formik.touched.paymentMode && formik.errors.paymentMode}
            value={formik.values.paymentMode}
            onChange={handlePaymentModeChange}
            onBlur={formik.handleBlur}
          >
            <MenuItem value={"ppe"}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Box pt={0.5}>
                  <img src="../images/p-pay1.png" alt="PhonePe" width="30px" />
                </Box>
                <Box>
                  <span>PhonePe</span>
                </Box>
              </Box>
            </MenuItem>
            <MenuItem value={"gpay"}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Box pt={0.5}>
                  <img src="../images/g-pay1.png" alt="G-Pay" width="30px" />
                </Box>
                <Box>
                  <span>G-Pay</span>
                </Box>
              </Box>
            </MenuItem>
            <MenuItem value={"paytm"}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Box pt={0.5}>
                  <img src="../images/paytm.png" alt="Paytm" width="30px" />
                </Box>
                <Box>
                  <span>Paytm</span>
                </Box>
              </Box>
            </MenuItem>
            <MenuItem value={"other"}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Box pt={0.5}>
                  <KeyboardIcon sx={{ width: "30px" }} />
                </Box>
                <Box>
                  <span>Other</span>
                </Box>
              </Box>
            </MenuItem>
          </Select>
        </Grid>
      </Grid>

      {/* Conditionally render the custom payment mode input field */}
      {isOtherPayment && (
        <Grid container mt={3}>
          <Grid item xs={6} sx={{ display: "flex", alignItems: "center" }}>
            <Typography>Enter Payment Mode :</Typography>
          </Grid>
          <Grid item xs={6}>
            <TextField
              size="small"
              id="customPaymentMode"
              name="customPaymentMode"
              value={formik.values.customPaymentMode}
              onChange={handleCustomPaymentModeChange}
              onBlur={formik.handleBlur}
            />
          </Grid>
        </Grid>
      )}
      <Typography
        my={2}
        sx={{ color: "green", fontSize: "14px", fontWeight: "bold" }}
      >
        **Choose Slip From Your Mobile Gallery**
      </Typography>

      <Grid container item xs={12}>
        {imagePreview ? (
          <Grid
            item
            xs={12}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mt: 2,
            }}
          >
            <Box sx={{ background: "#000", width: "400px", height: "200px" }}>
              {imagePreview.map((img, index) => (
                <img
                  width={"100%"}
                  height={"100%"}
                  key={index}
                  src={img}
                  alt={`Uploaded ${index}`}
                />
              ))}
            </Box>
          </Grid>
        ) : (
          <Grid item xs={12}>
            <Button
              variant="outlined"
              component="label"
              sx={{
                width: "100%",
                border: "2px dotted #212762",
                borderRadius: "4px",
                padding: "8px",
                height: "150px",
              }}
            >
              <AddCircleIcon sx={{ fontSize: "50px", color: "#212762" }} />
              <input
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                style={{ display: "none" }}
              />
            </Button>
          </Grid>
        )}
      </Grid>

      <Box sx={{ border: "1px dashed #e0e0e0", mt: 2 }} />
      <Box height={20} />

      <Box sx={{ display: "flex", flexDirection: "column" }}>
        <Box>
          <Button
            fullWidth
            sx={{
              textTransform: "capitalize",
              backgroundColor: "#1e2466",
              height: "100%",
              letterSpacing: 0.5,
            }}
            variant="contained"
            onClick={formik.handleSubmit}
            disabled={isSubmitting} // Disable button when submitting
          >
            {isSubmitting ? "Verifying..." : "Verify & Add Points"}
          </Button>
        </Box>
        <Box mt={1}>
          <Button
            fullWidth
            variant="outlined"
            color="error"
            onClick={() => setAddPointOpen(false)}
            sx={{ textTransform: "capitalize" }}
          >
            Cancel
          </Button>
        </Box>
      </Box>
      <Modal
        open={scannerPreviewOpen}
        onClose={() => setScannerPreviewOpen(false)}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 320,
            bgcolor: "#fff",
            borderRadius: 2,
            p: 2,
            boxShadow: 24,
            textAlign: "center",
          }}
        >
          <Typography variant="subtitle1" fontWeight="bold" mb={2}>
            Scan & Pay
          </Typography>
          <Typography variant="subtitle1" fontWeight="bold" mb={2}>
            {scannerImageName}
          </Typography>
          <Box
            component="img"
            src={scannerImage}
            alt="Scanner QR Full"
            sx={{
              width: "100%",
              borderRadius: 2,
              mb: 2,
            }}
          />

          <Button
            variant="contained"
            fullWidth
            sx={{ backgroundColor: "green" }}
            onClick={async () => {
              try {
                const token = sessionStorage.getItem("accesstoken");

                const response = await fetch(
                  `${api_url}wallet/download/${scannerImageFileName}`,
                  {
                    method: "GET",
                    headers: {
                      accesstoken: token,
                    },
                  }
                );

                if (!response.ok) {
                  throw new Error("Failed to fetch file");
                }

                const blob = await response.blob();
                saveAs(blob, scannerImageName); // Use file-saver
              } catch (err) {
                console.error("Download failed:", err);
              }
            }}
          >
            <DownloadIcon />
            Save & Send Money
          </Button>
          <Button
            onClick={() => setScannerPreviewOpen(false)}
            fullWidth
            sx={{ mt: 1 }}
            variant="outlined"
            color="error"
          >
            Close
          </Button>
        </Box>
      </Modal>
    </Box>
  );
}

export default AddPoints;
